import { createFileRoute } from "@tanstack/react-router";
import type { ClientStudioDocMetadata } from "api-types/studioDocs/studioDocMetadataTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import type { NotificationWithHTTPStatusCode } from "common/utils/fetchHelpers";
import { handleFetchError, objectToQueryParams } from "common/utils/fetchHelpers";
import { getOptionCatalogsMigrationURL } from "lib/endpointHelpers";
import { usePaginatedFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { migrateStudioDocOptions } from "lib/storage/optionSetStorage";
import { postWithTimeout, deleteWithTimeout, getWithTimeout } from "lib/utils/fetchAuthHelpers";
import { useMemo, useState } from "react";
import Button from "ui/buttons/Button";
import CodeBlock from "ui/components/CodeBlock";
import Modal from "ui/components/Modal";
import type { ModalProps } from "ui/components/Modal";
import useAppContext from "ui/hooks/useAppContext";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import styles from "./migrate-options.module.scss";

interface ValidateOptionsResultModalProps extends ModalProps {
  validationResults: Record<string, any>;
}

function ValidateOptionsResultModal({ open, onClose, title, validationResults }: ValidateOptionsResultModalProps) {
  return (
    <Modal open={open} onClose={onClose} title={title}>
      Left is the original overrides, right is the transformed overrides.
      <CodeBlock code={validationResults} />
    </Modal>
  );
}

export function MigrateOptionsFromDocToCatalog() {
  const { builderId } = useAppRouterParams();
  const { user } = useUserContext();
  const { setUserNotification } = useAppContext();
  const [showValidationResults, setShowValidationResults] = useState(false);
  const [validationResults, setValidationResults] = useState<Record<string, any>>({});
  const fetchKey = `${WEB_API_BASE_PATH}/studio-docs${objectToQueryParams({
    published: true,
    hideUntitled: true,
    builderId,
  })}`;
  const {
    data: studioDocsMetaData,
    pagination,
    setPagination,
    search,
    setSearch,
    orderBy,
    setOrderBy,
  } = usePaginatedFetch<ClientStudioDocMetadata>(fetchKey, [builderId, fetchKey], {
    refreshInterval: 5000,
  });

  const columns: Column<ClientStudioDocMetadata>[] = useMemo(
    () => [
      { header: "Name", field: "name", bold: true },
      {
        header: "Published",
        field: "publishedAt",
        fallback: true,
        type: "DATE",
      },
      {
        header: "Has Been Migrated",
        accessor: (doc) => (doc.studioOptionSetInstancesCount && doc.studioOptionSetInstancesCount > 0 ? "Yes" : "No"),
      },
    ],
    [],
  );

  return (
    <>
      <ConfigTable
        columns={columns}
        data={studioDocsMetaData?.data ?? []}
        pagination={pagination}
        setPagination={setPagination}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        totalCount={studioDocsMetaData?.totalCount}
        controls={{
          search: "Search Studio Docs",
          searchValue: search,
          setSearch,
          customControls: (
            <div className={styles.tableControls}>
              <Button
                type="button"
                btnStyle="secondary"
                btnSize="medium"
                tooltip="Checks which studio docs are locked by another user"
                onClick={async () => {
                  if (!builderId) {
                    setUserNotification({
                      messageType: "error",
                      message: "No builder ID found",
                    });
                    return;
                  }
                  if (!user) {
                    setUserNotification({
                      messageType: "error",
                      message: "No user context or ID found",
                    });
                    return;
                  }
                  try {
                    const res = await getWithTimeout(`${getOptionCatalogsMigrationURL(builderId)}/check-lock-status`);
                    if (res.ok) {
                      const lockResults = await res.json();
                      if (lockResults.studioDocsToCheck.length !== 0) {
                        console.log(JSON.stringify(lockResults, null, 2));
                        setUserNotification({
                          messageType: "info",
                          message: "Some studio docs are locked by another user, check console for list",
                        });
                      } else {
                        setUserNotification({
                          messageType: "success",
                          message: "All studio docs can be locked by current user",
                        });
                      }
                    } else {
                      throw res;
                    }
                  } catch (e) {
                    const err = await handleFetchError(e);
                    setUserNotification({
                      messageType: "error",
                      message: `Error checking studio doc lock status: ${err.message}`,
                    });
                  }
                }}
              >
                Check Lock Statuses
              </Button>
              <Button
                type="button"
                btnStyle="secondary"
                btnSize="medium"
                onClick={async () => {
                  if (!builderId) {
                    setUserNotification({
                      messageType: "error",
                      message: "Builder ID is required",
                    });
                    return;
                  }

                  const res = await postWithTimeout(
                    `${getOptionCatalogsMigrationURL(builderId)}/migrate-all`,
                    undefined,
                    30 * 1000,
                  );

                  if (res.ok) {
                    setUserNotification({
                      messageType: "success",
                      message: "Migrating all studio docs for this builder, this may take awhile.",
                    });
                  } else {
                    const errorData = await res.json().catch(() => ({}));
                    setUserNotification({
                      messageType: "error",
                      message: `Error migrating all docs: ${res.statusText}`,
                      statusCode: res.status,
                      ...errorData,
                    });
                  }
                }}
              >
                Migrate All
              </Button>
              <Button
                type="button"
                btnStyle="secondary"
                btnSize="medium"
                onClick={async () => {
                  if (!builderId) {
                    setUserNotification({
                      messageType: "error",
                      message: "Builder ID is required",
                    });
                    return;
                  }
                  setUserNotification({
                    messageType: "info",
                    message: "Validating migrated docs for selected builder, this may take a moment...",
                  });

                  const res = await postWithTimeout(
                    `${getOptionCatalogsMigrationURL(builderId)}/validate-all`,
                    undefined,
                    90 * 1000,
                  );

                  if (res.ok) {
                    const data = await res.json();
                    setValidationResults(data);
                    setShowValidationResults(true);
                  } else {
                    setUserNotification({
                      messageType: "error",
                      message: `Error deleting migration data: ${res.statusText}`,
                    });
                  }
                }}
              >
                Validate All Migrations
              </Button>
              <Button
                type="button"
                btnStyle="remove"
                btnSize="medium"
                onClick={async () => {
                  if (!builderId) {
                    setUserNotification({
                      messageType: "error",
                      message: "Builder ID is required",
                    });
                    return;
                  }

                  setUserNotification({
                    messageType: "info",
                    message: "Triggered builder deletion, this may take a moment...",
                  });
                  const res = await deleteWithTimeout(
                    `${getOptionCatalogsMigrationURL(builderId)}/delete-all`,
                    undefined,
                    60 * 1000,
                  );

                  if (res.ok) {
                    const data = await res.json();
                    setUserNotification({
                      messageType: "success",
                      message: `Successfully deleted migrated builder data: ${data.message}`,
                    });
                  } else {
                    setUserNotification({
                      messageType: "error",
                      message: `Error deleting migration data: ${res.statusText}`,
                    });
                  }
                }}
              >
                Delete All Migrations
              </Button>
            </div>
          ),
        }}
        multiSelect={(rows) => {
          return (
            <div className={styles.multiselect}>
              <Button
                type="button"
                btnStyle="primary"
                btnSize="large"
                onClick={async () => {
                  if (!builderId) {
                    setUserNotification({
                      messageType: "error",
                      message: "Builder ID is required",
                    });
                    return;
                  }
                  let notification: NotificationWithHTTPStatusCode | null = null;
                  for (const row of rows) {
                    const notificationInstance = await migrateStudioDocOptions({
                      studioDocId: row.original.id,
                      builderId,
                    });
                    if (notificationInstance) {
                      notification = notificationInstance;
                    } else {
                      row.toggleSelected(false);
                    }
                  }
                  setUserNotification(
                    notification ?? {
                      messageType: "success",
                      message: `Migrating options for ${rows.length} docs; please wait a minute`,
                    },
                  );
                }}
              >
                Migrate options
              </Button>
              {
                // Show if migrated
                rows.some(
                  (row) => row.original.studioOptionSetInstancesCount && row.original.studioOptionSetInstancesCount > 0,
                ) && (
                  <Button
                    type="button"
                    btnStyle="primary"
                    btnSize="large"
                    onClick={async () => {
                      if (!builderId) {
                        setUserNotification({
                          messageType: "error",
                          message: "Builder ID is required",
                        });
                        return;
                      }
                      setUserNotification({
                        messageType: "success",
                        message: `Validating data; please wait a minute`,
                      });

                      // Filter out docs that haven't been migrated otherwise validation will return everything as a diff.
                      const studioDocIds = rows
                        .filter(
                          (row) =>
                            row.original.studioOptionSetInstancesCount &&
                            row.original.studioOptionSetInstancesCount > 0,
                        )
                        .map((row) => row.original.id);

                      // Technically this should never happen, but just in case.
                      if (studioDocIds.length === 0) {
                        setUserNotification({
                          messageType: "error",
                          message: "No studio docs to validate. Please migrate options first.",
                        });
                        return;
                      }

                      const res = await postWithTimeout(
                        `${getOptionCatalogsMigrationURL(
                          builderId,
                        )}/validate-migration?studioDocIds=${studioDocIds.join(",")}`,
                        undefined,
                        60 * 1000,
                      );

                      if (res.ok) {
                        const data = await res.json();
                        setValidationResults(data);
                        setShowValidationResults(true);
                      } else {
                        setUserNotification({
                          messageType: "error",
                          message: `Error validating data: ${res.statusText}`,
                        });
                      }
                    }}
                  >
                    Validate overrides
                  </Button>
                )
              }
            </div>
          );
        }}
      />
      {showValidationResults && (
        <ValidateOptionsResultModal
          open
          onClose={() => {
            setShowValidationResults(false);
            setValidationResults({});
          }}
          title="Validation Results"
          validationResults={validationResults}
        />
      )}
    </>
  );
}

export const Route = createFileRoute("/debug/option-migration/migrate-options")({
  component: MigrateOptionsFromDocToCatalog,
});
